import React, { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useToast } from "../../../components/Toast";
import { Loading } from "../../../components/Loading";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useFirebaseAuth } from "../../../hooks/useFirebaseAuth";
import { commonButtonProps, commonTextFieldProps } from "../../../utils/styles";
import NavigationHeader from "../../../components/NavigationHeader";

interface ServiceOperatorForm {
  id: string | null;
  companyId: string;
  name: string;
  contactNumber: string;
  contactPerson: string;
  address: string;
  gstNumber: string | null;
  gstPercentage: number | null;
  status: "ACTIVE" | "INACTIVE" | "BLACKLISTED";
  notes: string;
  type: "INHOUSE" | "EXTERNAL" | "ONLINE AGENT";
  operationType: "BUS" | "LUGGAGE" | "OTHER" | string;
  otherOperationType?: string;
  pickupLocation?: string | null;
  pricingType?: string | null;
  pricingValue?: string | null;
}

const initialFormState: ServiceOperatorForm = {
  id: null,
  companyId: "",
  name: "",
  contactNumber: "",
  contactPerson: "",
  address: "",
  gstNumber: null,
  gstPercentage: 0,
  status: "ACTIVE",
  notes: "",
  type: "INHOUSE",
  operationType: "BUS",
  otherOperationType: "",
  pickupLocation: null,
  pricingType: null,
  pricingValue: null,
};

const operationTypes = ["BUS", "LUGGAGE"];
const operatorTypes = ["INHOUSE", "OFFLINE AGENT"];
const busOperatorTypes = [
  "INHOUSE",
  "ONLINE AGENT (OTA)",
  "OFFLINE AGENT",
  "SECONDARY AGENT (BRANCH)",
];
const statusTypes = ["ACTIVE", "INACTIVE", "BLACKLISTED"];

// Validation Schema
const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  contactNumber: Yup.string()
    .matches(/^\d{10}$/, "Contact number must be exactly 10 digits")
    .required("Contact number is required"),
  contactPerson: Yup.string().required("Contact Person Name is required"),
  gstNumber: Yup.string()
    .nullable()
    .matches(
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
      "Invalid GST Number format. Example: 29ABCDE1234F1Z5"
    ),
  gstPercentage: Yup.number()
    .nullable()
    .optional()
    .min(0, "GST percentage must be greater than or equal to 0")
    .max(100, "GST percentage must be less than or equal to 100"),
  address: Yup.string().optional(),
  notes: Yup.string(),
  type: Yup.string().required("Type is required"),
  operationType: Yup.string().required("Operation Type is required"),
  status: Yup.string().required("Status is required"),
  pricingType: Yup.string().optional(),
  pricingValue: Yup.string().when("pricingType", {
    is: (val: string) => val && val.length > 0,
    then: (schema) =>
      schema.required(
        "Commission value is required when commission type is selected"
      ),
    otherwise: (schema) => schema.optional(),
  }),
});

const addServiceOperator = async (
  formData: ServiceOperatorForm,
  user: any,
  showToast: any,
  navigate: any,
  setIsSubmitting: any
) => {
  try {
    setIsSubmitting(true);
    const url = `${process.env.REACT_APP_API_HOST}service-operators`;
    const token = user?.accessToken;

    const { id, ...formDataWithoutId } = formData;
    const enrichedData = {
      ...formDataWithoutId,
      companyId: user.companyId,
    };

    const response = await axios.post(url, enrichedData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.data?.isSuccess) {
      showToast("Operator created successfully", "success");
      navigate("/master-data/operators");
    } else {
      throw new Error(response.data?.message);
    }
  } catch (error) {
    showToast("Failed to save operator", "error");
  } finally {
    setIsSubmitting(false);
  }
};

const fetchServiceOperator = async (
  operatorId: string,
  user: any,
  setIsLoading: any,
  showToast: any
) => {
  try {
    setIsLoading(true);
    const url = `${process.env.REACT_APP_API_HOST}service-operators/${operatorId}`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${user.accessToken}` },
    });
    if (response.data?.isSuccess) {
      return response.data?.data;
    } else {
      throw new Error(response.data?.message);
    }
  } catch (error) {
    showToast("Failed to fetch operator", "error");
  } finally {
    setIsLoading(false);
  }
};

const updateServiceOperator = async (
  formData: ServiceOperatorForm,
  user: any,
  showToast: any,
  navigate: any,
  setIsSubmitting: any
) => {
  try {
    setIsSubmitting(true);
    const url = `${process.env.REACT_APP_API_HOST}service-operators/${formData.id}`;

    const enrichedData = {
      ...formData,
      ...(formData.operationType === "OTHER" && {
        operationType: formData.otherOperationType,
      }),
      companyId: user.companyId,
    };

    const response = await axios.put(url, enrichedData, {
      headers: {
        Authorization: `Bearer ${user.accessToken}`,
      },
    });

    if (response.data?.isSuccess) {
      showToast("Operator updated successfully", "success");
      navigate("/master-data/operators");
    } else {
      throw new Error(response.data?.message);
    }
  } catch (error) {
    showToast("Failed to updating operator", "error");
  } finally {
    setIsSubmitting(false);
  }
};

const AddServiceOperator = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { showToast } = useToast();
  const { operatorId = null } = useParams();
  const { user, loading: authLoading } = useFirebaseAuth();
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    if (!user || authLoading) return;
    const loadOperator = async () => {
      setIsEdit(operatorId !== "new" && operatorId !== null);
      if (operatorId !== "new" && operatorId !== null) {
        const operator = await fetchServiceOperator(
          operatorId,
          user,
          setIsLoading,
          showToast
        );
        if (operator) {
          setInitialValues(operator);
        }
      }
      setIsLoading(false);
    };
    loadOperator();
  }, [user]);

  const [initialValues, setInitialValues] =
    useState<ServiceOperatorForm>(initialFormState);

  const handleSubmit = async (values: ServiceOperatorForm) => {
    setIsSubmitting(true);
    try {
      if (operatorId && operatorId !== "new") {
        await updateServiceOperator(
          values,
          user,
          showToast,
          navigate,
          setIsSubmitting
        );
      } else {
        await addServiceOperator(
          values,
          user,
          showToast,
          navigate,
          setIsSubmitting
        );
      }
    } catch (error) {
      showToast("An error occurred while saving", "error");
    }
    setIsSubmitting(false);
  };

  if (isSubmitting || isLoading) {
    return isSubmitting && operatorId !== "new" ? (
      <Loading message="Updating operator..." fullScreen={true} />
    ) : isSubmitting && operatorId === "new" ? (
      <Loading message="Creating operator..." fullScreen={true} />
    ) : (
      <Loading message="Loading operator..." fullScreen={true} />
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <NavigationHeader
        breadcrumbs={[
          { label: "Master Data", path: "/master-data" },
          { label: "Operator/Agent", path: "/master-data/operators" },
          { label: isEdit ? "Edit" : "Add" },
        ]}
        title={isEdit ? "Edit Operator/Agent" : "Add Operator/Agent"}
      />
      <Paper elevation={2} sx={{ p: 2 }}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, handleChange, values, setFieldValue }) => (
            <Form>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ fontSize: "0.9rem" }}>
                    Operator/Agent Information
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <Field
                    as={TextField}
                    fullWidth
                    required
                    label="Name"
                    name="name"
                    error={touched.name && !!errors.name}
                    helperText={touched.name && errors.name}
                    onChange={handleChange}
                    value={values.name}
                    {...commonTextFieldProps}
                  />
                </Grid>

                <Grid item xs={12} sm={2}>
                  {isEdit && values.operationType !== "" ? (
                    <Field
                      as={TextField}
                      label="Operation Type"
                      name="operationType"
                      fullWidth
                      value={values.operationType}
                      required
                      {...commonTextFieldProps}
                    />
                  ) : (
                    <Field
                      as={TextField}
                      select
                      fullWidth
                      required
                      label="Operation Type"
                      name="operationType"
                      value={values.operationType}
                      error={touched.operationType && !!errors.operationType}
                      helperText={touched.operationType && errors.operationType}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFieldValue("operationType", e.target.value);
                        if (e.target.value !== "OTHER") {
                          setFieldValue("otherOperationType", "");
                        }
                        if (e.target.value === "BUS") {
                          setFieldValue("type", "OFFLINE AGENT");
                        }
                      }}
                      {...commonTextFieldProps}
                    >
                      {operationTypes.map((type) => (
                        <MenuItem
                          key={type}
                          value={type}
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {type}
                        </MenuItem>
                      ))}
                    </Field>
                  )}
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Field
                    as={TextField}
                    select
                    fullWidth
                    required
                    label="Type"
                    name="type"
                    value={values.type}
                    error={touched.type && !!errors.type}
                    helperText={touched.type && errors.type}
                    onChange={handleChange}
                    {...commonTextFieldProps}
                  >
                    {values.operationType === "BUS"
                      ? busOperatorTypes.map((type) => {
                          return (
                            <MenuItem
                              key={type}
                              value={type}
                              sx={{ fontSize: "0.75rem" }}
                            >
                              {type}
                            </MenuItem>
                          );
                        })
                      : operatorTypes.map((type) => {
                          return (
                            <MenuItem
                              key={type}
                              value={type}
                              sx={{ fontSize: "0.75rem" }}
                            >
                              {type}
                            </MenuItem>
                          );
                        })}
                  </Field>
                </Grid>

                <Grid item xs={12} sm={2}>
                  <Field
                    as={TextField}
                    select
                    fullWidth
                    required
                    label="Status"
                    name="status"
                    value={values.status}
                    error={touched.status && !!errors.status}
                    helperText={touched.status && errors.status}
                    onChange={handleChange}
                    {...commonTextFieldProps}
                  >
                    {statusTypes.map((status) => (
                      <MenuItem
                        key={status}
                        value={status}
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {status}
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Location"
                    placeholder="Enter location"
                    name="pickupLocation"
                    onChange={handleChange}
                    value={values.pickupLocation}
                    {...commonTextFieldProps}
                  />
                </Grid>

                <Grid item xs={4}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="GST Number"
                    name="gstNumber"
                    onChange={handleChange}
                    value={values.gstNumber}
                    error={touched.gstNumber && !!errors.gstNumber}
                    helperText={touched.gstNumber && errors.gstNumber}
                    {...commonTextFieldProps}
                  />
                </Grid>

                <Grid item xs={2}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="GST Percentage"
                    name="gstPercentage"
                    type="number"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      let value = e.target.value;
                      // Remove leading zeros while typing, but allow empty string temporarily
                      if (value && !isNaN(Number(value))) {
                        value = parseInt(value).toString();
                      }
                      setFieldValue("gstPercentage", value);
                    }}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                      // If empty or invalid, set to 0
                      if (
                        !e.target.value ||
                        e.target.value === "" ||
                        isNaN(Number(e.target.value))
                      ) {
                        setFieldValue("gstPercentage", 0);
                      }
                    }}
                    value={values.gstPercentage}
                    error={touched.gstPercentage && !!errors.gstPercentage}
                    helperText={touched.gstPercentage && errors.gstPercentage}
                    InputProps={{
                      inputProps: {
                        min: 0,
                        max: 100,
                        step: "any",
                        onWheel: (e: React.WheelEvent<HTMLInputElement>) =>
                          (e.target as HTMLInputElement).blur(),
                      },
                    }}
                    {...commonTextFieldProps}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ fontSize: "0.9rem" }}>
                    Pricing Information
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Field
                    as={TextField}
                    select
                    fullWidth
                    label="Pricing Type"
                    name="pricingType"
                    value={values.pricingType}
                    error={touched.pricingType && !!errors.pricingType}
                    helperText={touched.pricingType && errors.pricingType}
                    onChange={handleChange}
                    {...commonTextFieldProps}
                  >
                    {["Percentage", "Fixed"].map((type) => (
                      <MenuItem
                        key={type}
                        value={type}
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {type}
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Field
                    as={TextField}
                    fullWidth
                    label={
                      values.pricingType === "Fixed"
                        ? "Commission per item"
                        : "Commission percentage"
                    }
                    name="pricingValue"
                    type="number"
                    value={values.pricingValue}
                    error={touched.pricingValue && !!errors.pricingValue}
                    helperText={touched.pricingValue && errors.pricingValue}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment:
                        values.pricingType === "Fixed" ? (
                          <span style={{ marginRight: 8, fontSize: "0.75rem" }}>
                            ₹
                          </span>
                        ) : null,
                      endAdornment:
                        values.pricingType === "Percentage" ? (
                          <span style={{ marginLeft: 8, fontSize: "0.75rem" }}>
                            %
                          </span>
                        ) : null,
                      inputProps: {
                        style: { textAlign: "left" },
                        onWheel: (e: React.WheelEvent<HTMLInputElement>) =>
                          (e.target as HTMLInputElement).blur(),
                      },
                    }}
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ fontSize: "0.9rem" }}>
                    Contact Information
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    required
                    label="Contact Person"
                    name="contactPerson"
                    error={touched.contactPerson && !!errors.contactPerson}
                    helperText={touched.contactPerson && errors.contactPerson}
                    onChange={handleChange}
                    value={values.contactPerson}
                    {...commonTextFieldProps}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    required
                    label="Contact Number"
                    name="contactNumber"
                    type="tel"
                    inputProps={{
                      inputMode: "numeric",
                      maxLength: 10,
                    }}
                    error={touched.contactNumber && !!errors.contactNumber}
                    helperText={touched.contactNumber && errors.contactNumber}
                    onChange={handleChange}
                    value={values.contactNumber}
                    {...commonTextFieldProps}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Address"
                    name="address"
                    multiline
                    rows={2}
                    onChange={handleChange}
                    value={values.address}
                    error={touched.address && !!errors.address}
                    helperText={touched.address && errors.address}
                    {...commonTextFieldProps}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Notes"
                    name="notes"
                    multiline
                    rows={2}
                    onChange={handleChange}
                    value={values.notes}
                    {...commonTextFieldProps}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" gap={2} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      onClick={() => navigate("/master-data/operators")}
                      {...commonButtonProps}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      {...commonButtonProps}
                    >
                      Save Operator/Agent
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Paper>
    </Container>
  );
};

export default AddServiceOperator;
